/**
 * Composable for audio playlist navigation with loop and shuffle support
 */

import { ref, computed, watch, onUnmounted, type Ref, type ComputedRef } from 'vue'
import type { AudioAssetListItem } from '../../types/asset'

export interface UseAudioNavigationOptions {
  items: Ref<AudioAssetListItem[]>
  initialIndex?: number
  loop?: boolean
  shuffle?: boolean
  autoAdvance?: boolean
  onIndexChange?: (index: number) => void
  onTrackEnd?: (index: number) => void
  onCollectionEnd?: () => void
}

export interface UseAudioNavigationReturn {
  // State
  currentIndex: Ref<number>
  currentItem: ComputedRef<AudioAssetListItem | undefined>
  totalItems: ComputedRef<number>

  // Navigation computed
  canGoNext: ComputedRef<boolean>
  canGoPrev: ComputedRef<boolean>
  isFirstItem: ComputedRef<boolean>
  isLastItem: ComputedRef<boolean>

  // Loop and shuffle state
  isLoop: Ref<boolean>
  isShuffle: Ref<boolean>
  isAutoAdvance: Ref<boolean>

  // Navigation methods
  goToIndex: (index: number) => void
  next: () => boolean
  prev: () => void
  first: () => void
  last: () => void

  // Loop and shuffle methods
  toggleLoop: () => void
  toggleShuffle: () => void
  setLoop: (enabled: boolean) => void
  setShuffle: (enabled: boolean) => void
  toggleAutoAdvance: () => void
  setAutoAdvance: (enabled: boolean) => void

  // Track end handling
  handleTrackEnd: () => void
}

/**
 * Composable for managing audio playlist navigation
 */
export function useAudioNavigation(options: UseAudioNavigationOptions): UseAudioNavigationReturn {
  const {
    items,
    initialIndex = 0,
    loop = false,
    shuffle = false,
    autoAdvance = true,
    onIndexChange,
    onTrackEnd,
    onCollectionEnd,
  } = options

  // Navigation state
  const currentIndex = ref(initialIndex)
  const isLoop = ref(loop)
  const isShuffle = ref(shuffle)
  const isAutoAdvance = ref(autoAdvance)

  // Shuffle state
  const shuffleOrder = ref<number[]>([])
  let shufflePosition = 0

  // Computed
  const totalItems = computed(() => items.value.length)

  const currentItem = computed(() => items.value[currentIndex.value])

  const canGoNext = computed(() => {
    if (totalItems.value === 0) return false
    return isLoop.value || currentIndex.value < totalItems.value - 1
  })

  const canGoPrev = computed(() => {
    if (totalItems.value === 0) return false
    return isLoop.value || currentIndex.value > 0
  })

  const isFirstItem = computed(() => currentIndex.value === 0)
  const isLastItem = computed(() => currentIndex.value === totalItems.value - 1)

  /**
   * Generates a shuffled order of indices (Fisher-Yates algorithm)
   */
  function generateShuffleOrder(): void {
    const indices = Array.from({ length: totalItems.value }, (_, i) => i)
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[indices[i], indices[j]] = [indices[j], indices[i]]
    }
    shuffleOrder.value = indices
    shufflePosition = shuffleOrder.value.indexOf(currentIndex.value)
    if (shufflePosition === -1) shufflePosition = 0
  }

  /**
   * Navigates to a specific index
   */
  function goToIndex(index: number): void {
    if (totalItems.value === 0) return

    let validIndex = index

    if (isLoop.value) {
      // Wrap around
      if (index < 0) {
        validIndex = totalItems.value - 1
      } else if (index >= totalItems.value) {
        validIndex = 0
      }
    } else {
      // Clamp to bounds
      validIndex = Math.max(0, Math.min(index, totalItems.value - 1))
    }

    if (validIndex !== currentIndex.value) {
      currentIndex.value = validIndex
      onIndexChange?.(validIndex)

      // Update shuffle position
      if (isShuffle.value) {
        shufflePosition = shuffleOrder.value.indexOf(validIndex)
        if (shufflePosition === -1) {
          generateShuffleOrder()
        }
      }
    }
  }

  /**
   * Navigates to the next track
   * @returns true if navigation occurred, false if at end without loop
   */
  function next(): boolean {
    if (!canGoNext.value && !isLoop.value) {
      return false
    }

    if (isShuffle.value && shuffleOrder.value.length > 0) {
      shufflePosition = (shufflePosition + 1) % shuffleOrder.value.length
      goToIndex(shuffleOrder.value[shufflePosition])
    } else {
      goToIndex(currentIndex.value + 1)
    }

    return true
  }

  /**
   * Navigates to the previous track
   */
  function prev(): void {
    if (!canGoPrev.value) return

    if (isShuffle.value && shuffleOrder.value.length > 0) {
      shufflePosition = (shufflePosition - 1 + shuffleOrder.value.length) % shuffleOrder.value.length
      goToIndex(shuffleOrder.value[shufflePosition])
    } else {
      goToIndex(currentIndex.value - 1)
    }
  }

  /**
   * Navigates to the first track
   */
  function first(): void {
    if (isShuffle.value) {
      shufflePosition = 0
      goToIndex(shuffleOrder.value[0])
    } else {
      goToIndex(0)
    }
  }

  /**
   * Navigates to the last track
   */
  function last(): void {
    if (isShuffle.value) {
      shufflePosition = shuffleOrder.value.length - 1
      goToIndex(shuffleOrder.value[shufflePosition])
    } else {
      goToIndex(totalItems.value - 1)
    }
  }

  /**
   * Toggles loop mode
   */
  function toggleLoop(): void {
    isLoop.value = !isLoop.value
  }

  /**
   * Toggles shuffle mode
   */
  function toggleShuffle(): void {
    isShuffle.value = !isShuffle.value
    if (isShuffle.value) {
      generateShuffleOrder()
    }
  }

  /**
   * Sets loop mode
   */
  function setLoop(enabled: boolean): void {
    isLoop.value = enabled
  }

  /**
   * Sets shuffle mode
   */
  function setShuffle(enabled: boolean): void {
    isShuffle.value = enabled
    if (enabled && shuffleOrder.value.length === 0) {
      generateShuffleOrder()
    }
  }

  /**
   * Toggles auto-advance (play the next track automatically when one ends)
   */
  function toggleAutoAdvance(): void {
    isAutoAdvance.value = !isAutoAdvance.value
  }

  /**
   * Sets auto-advance mode
   */
  function setAutoAdvance(enabled: boolean): void {
    isAutoAdvance.value = enabled
  }

  /**
   * Handles track end event - auto-advances to next track
   * Returns true if the same track should be restarted (single-track loop)
   */
  function handleTrackEnd(): boolean {
    onTrackEnd?.(currentIndex.value)

    // Single track with loop: restart it regardless of auto-advance — looping a
    // lone track is a loop concern, not a "play next track" one.
    if (isLoop.value && totalItems.value <= 1) {
      return true
    }

    if (!isAutoAdvance.value) return false

    // If not at end or looping, advance to next
    if (canGoNext.value) {
      next()
    } else if (!isLoop.value) {
      // Collection ended
      onCollectionEnd?.()
    }

    return false
  }

  // Watch for items changes
  watch(
    () => items.value.length,
    (newLength) => {
      // Reset index if out of bounds
      if (currentIndex.value >= newLength) {
        currentIndex.value = Math.max(0, newLength - 1)
      }
      // Regenerate shuffle order if shuffling
      if (isShuffle.value) {
        generateShuffleOrder()
      }
    },
  )

  // Initialize shuffle order if needed
  if (shuffle && totalItems.value > 0) {
    generateShuffleOrder()
  }

  return {
    // State
    currentIndex,
    currentItem,
    totalItems,

    // Navigation computed
    canGoNext,
    canGoPrev,
    isFirstItem,
    isLastItem,

    // Loop and shuffle state
    isLoop,
    isShuffle,
    isAutoAdvance,

    // Navigation methods
    goToIndex,
    next,
    prev,
    first,
    last,

    // Loop and shuffle methods
    toggleLoop,
    toggleShuffle,
    setLoop,
    setShuffle,
    toggleAutoAdvance,
    setAutoAdvance,

    // Track end handling
    handleTrackEnd,
  }
}
